"use client";

import { useEffect, useState } from "react";
import type { UserTagSuggestion } from "@/components/posts/user-tag-picker";
import { Label } from "@/components/ui/label";
import {
  type Participant,
  suggestionToParticipant,
  TeamCard,
  ValidationWarning,
} from "./base-match-template";
import { ActivityCourtPreview, ActivityDisplayPreview } from "./previews";
import { SquashScoreEditor } from "./score-editors/squash-score-editor";

// ──────────────────────────────────────────────────────────────────────────────
// TYPES
// ──────────────────────────────────────────────────────────────────────────────

export type SquashTemplateData = {
  matchScore: { yourGames: number; opponentGames: number };
  games?: Array<{ yourScore: number; opponentScore: number }>;
  positions: Record<string, "your-team" | "opponent-team">;
  hasScore?: boolean;
};

export type SquashParticipant = Participant;

// ──────────────────────────────────────────────────────────────────────────────
// COMPONENT
// ──────────────────────────────────────────────────────────────────────────────

type SquashTemplateProps = {
  currentUser: SquashParticipant;
  onChange: (
    data: SquashTemplateData,
    participants: SquashParticipant[],
  ) => void;
  initialData?: Partial<SquashTemplateData>;
};

export function SquashTemplate({
  currentUser,
  onChange,
  initialData,
}: SquashTemplateProps) {
  const [participants, setParticipants] = useState<SquashParticipant[]>([
    currentUser,
  ]);
  const [teams, setTeams] = useState<
    Record<string, "your-team" | "opponent-team">
  >(() => ({
    [currentUser.id]: "your-team",
    ...(initialData?.positions ?? {}),
  }));
  type SquashScorePart = Pick<SquashTemplateData, "matchScore" | "games">;
  const [scorePart, setScorePart] = useState<SquashScorePart>(() => ({
    matchScore: initialData?.matchScore ?? { yourGames: 0, opponentGames: 0 },
    games: initialData?.games ?? [],
  }));

  const yourTeam = participants.filter((p) => teams[p.id] === "your-team");
  const opponentTeam = participants.filter(
    (p) => teams[p.id] === "opponent-team",
  );
  const selectedUserIds = new Set(participants.map((p) => p.id));

  useEffect(() => {
    const data: SquashTemplateData = {
      matchScore: scorePart.matchScore,
      games: scorePart.games ?? [],
      positions: teams,
      hasScore: true,
    };
    onChange(data, participants);
  }, [scorePart, teams, participants, onChange]);

  const yourGames = scorePart.matchScore.yourGames;
  const opponentGames = scorePart.matchScore.opponentGames;

  const handleAddParticipant = (
    suggestion: UserTagSuggestion,
    team: "your-team" | "opponent-team",
  ) => {
    if (participants.length >= 2) return;
    const teamCount =
      team === "your-team" ? yourTeam.length : opponentTeam.length;
    if (teamCount >= 1) return;

    const newParticipant = suggestionToParticipant(suggestion);
    setParticipants((prev) => [...prev, newParticipant]);
    setTeams((prev) => ({ ...prev, [suggestion.id]: team }));
  };

  const handleRemoveParticipant = (userId: string) => {
    if (userId === currentUser.id) return;
    setParticipants((prev) => prev.filter((p) => p.id !== userId));
    setTeams((prev) => {
      const next = { ...prev };
      delete next[userId];
      return next;
    });
  };

  const yourWon = yourGames > opponentGames;
  const opponentWon = opponentGames > yourGames;
  const isDraw = yourGames === opponentGames && yourGames > 0;
  const isComplete = yourTeam.length === 1 && opponentTeam.length === 1;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-3">
        <TeamCard
          label="Team 1"
          isYourTeam
          players={yourTeam}
          maxPlayers={1}
          currentUserId={currentUser.id}
          selectedUserIds={selectedUserIds}
          onAddPlayer={(s) => handleAddParticipant(s, "your-team")}
          onRemovePlayer={handleRemoveParticipant}
          highlight={yourWon || isDraw}
          isComplete={yourTeam.length === 1}
        />
        <TeamCard
          label="Team 2"
          isYourTeam={false}
          players={opponentTeam}
          maxPlayers={1}
          currentUserId={currentUser.id}
          selectedUserIds={selectedUserIds}
          onAddPlayer={(s) => handleAddParticipant(s, "opponent-team")}
          onRemovePlayer={handleRemoveParticipant}
          highlight={opponentWon}
          isComplete={opponentTeam.length === 1}
        />
      </div>

      {!isComplete && <ValidationWarning message="Add an opponent to post" />}

      <SquashScoreEditor
        initialData={initialData as unknown as Record<string, unknown>}
        onChange={(data) => setScorePart(data as unknown as SquashScorePart)}
      />

      {participants.length >= 2 && (
        <div className="space-y-2">
          <Label className="text-text-primary">Preview</Label>
          <ActivityCourtPreview
            activityId="squash"
            yourTeam={yourTeam}
            opponentTeam={opponentTeam}
            yourWon={yourWon}
          />
        </div>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// DISPLAY COMPONENT (for feed)
// ──────────────────────────────────────────────────────────────────────────────

type SquashDisplayProps = {
  data: SquashTemplateData | null;
  participants: {
    id: string;
    displayName: string;
    avatarUrl: string | null;
    username: string | null;
  }[];
};

export function SquashDisplay({ data, participants }: SquashDisplayProps) {
  return (
    <ActivityDisplayPreview
      activityId="squash"
      data={data}
      participants={participants}
    />
  );
}
